import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useInjection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { BuildingsIcon } from '@phosphor-icons/react';
import { LocalStorageStore } from '@stores';

import { DI_TOKENS } from '../../../constants';

/**
 * Greeting header for the dashboard: personalised welcome, the org name +
 * compliance-overview subtitle, and today's date (locale-aware).
 */
export const WelcomeBar: FC = observer(() => {
  const { t, i18n } = useTrans();
  const localStorageStore = useInjection<LocalStorageStore>(DI_TOKENS.LocalStorageStore);
  const userProfile = localStorageStore.userProfile;

  const firstName = userProfile?.user?.firstName ?? '';
  const orgName = userProfile?.tenant?.name;

  const today = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date());

  return (
    <Box
      sx={{
        px: { xs: 2.5, md: 3.75 },
        pt: 2.75,
        pb: 2.25,
        borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 2,
        flexShrink: 0,
        flexWrap: 'wrap',
      }}
    >
      <Box>
        <Typography variant="h1" sx={{ fontSize: 24, fontWeight: 700, mb: 0.5 }}>
          {t('app.dashboard.welcome.greeting', { name: firstName })}
        </Typography>
        <Typography
          sx={{ fontSize: 13, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.75 }}
        >
          <BuildingsIcon size={15} />
          {orgName
            ? `${orgName} · ${t('app.dashboard.welcome.subtitle')}`
            : t('app.dashboard.welcome.subtitle')}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: 'text.tertiary' }}>
        {t('app.dashboard.welcome.today', { date: today })}
      </Typography>
    </Box>
  );
});
